import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useLookupCompanyByNip } from "hooks/companies";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface NipAutofillFields {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
}

interface NipAutofillProps {
  nipFieldName?: string;
  fields: NipAutofillFields;
  enabled?: boolean;
}

const NipAutofill: React.FC<NipAutofillProps> = ({
  nipFieldName = "nip",
  fields,
  enabled = true,
}) => {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const lastLookedUpRef = useRef<string | null>(null);
  const { mutate: lookupCompany } = useLookupCompanyByNip({
    onSuccess: (data) => {
      if (fields.name) setFieldValue(fields.name, data.name);
      if (fields.address && data.address) {
        setFieldValue(fields.address, data.address);
      }
      if (fields.postalCode && data.postalCode) {
        setFieldValue(fields.postalCode, data.postalCode);
      }
      if (fields.city && data.city) setFieldValue(fields.city, data.city);
    },
  });

  const rawNip = values?.[nipFieldName];

  useEffect(() => {
    const nip =
      typeof rawNip === "string" ? rawNip.replace(/\D/g, "") : "";

    if (!enabled || !isValidPolishNip(nip)) {
      lastLookedUpRef.current = null;
      return;
    }
    if (lastLookedUpRef.current === nip) return;

    const timeout = setTimeout(() => {
      lastLookedUpRef.current = nip;
      lookupCompany(nip);
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawNip, enabled]);

  return null;
};

export default NipAutofill;
